import { useEffect, useRef, useState, useCallback } from 'react';

export const useAutoScroll = (messages, selectedChat) => {
  const chatContainerRef = useRef(null);
  const lastUserMessageIdRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const scrollPositionAfterSendRef = useRef(null);
  const hasScrolledToBottomRef = useRef(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const [newMessageId, setNewMessageId] = useState(null);

  // Helper to find scroll parent
  const findScrollParent = useCallback(() => {
    const firstMessage = document.querySelector('[data-message-id]');
    if (!firstMessage) return null;

    let scrollParent = firstMessage.parentElement;
    while (scrollParent) {
      const style = window.getComputedStyle(scrollParent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return scrollParent;
      }
      scrollParent = scrollParent.parentElement;
    }
    return null;
  }, []);

  // Scroll to bottom when messages first load for a chat
  useEffect(() => {
    if (messages.length === 0 || hasScrolledToBottomRef.current || initialLoadDoneRef.current) {
      return;
    }

    // Messages loaded for the first time - scroll to bottom
    hasScrolledToBottomRef.current = true;

    setTimeout(() => {
      const scrollParent = findScrollParent();
      if (scrollParent) {
        scrollParent.scrollTop = scrollParent.scrollHeight;
      }

      // Mark initial load as done after scroll
      setTimeout(() => {
        initialLoadDoneRef.current = true;
      }, 300);
    }, 100);
  }, [messages, findScrollParent]);

  // Scroll a user message to the top of the chat window
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    // Only scroll when a NEW user message appears AND after initial load
    if (
      lastMessage.role === 'user' &&
      lastUserMessageIdRef.current !== lastMessage.id &&
      initialLoadDoneRef.current
    ) {
      lastUserMessageIdRef.current = lastMessage.id;
      setHasUserSentMessage(true);
      setNewMessageId(lastMessage.id);

      // Clear the animation flag after animation completes
      setTimeout(() => {
        setNewMessageId(null);
      }, 600);

      // Wait for DOM to update, then find and scroll to the element
      setTimeout(() => {
        const element = document.querySelector(`[data-message-id="${lastMessage.id}"]`);
        if (!element) return;

        const scrollParent = findScrollParent();

        if (scrollParent) {
          const elementRect = element.getBoundingClientRect();
          const containerRect = scrollParent.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top + scrollParent.scrollTop;

          // Smooth scroll to position the message at top
          scrollParent.scrollTo({
            top: relativeTop - 16,
            behavior: 'smooth'
          });

          // Store the scroll position after animation completes
          setTimeout(() => {
            scrollPositionAfterSendRef.current = scrollParent.scrollTop;
          }, 500);
        }
      }, 100);
    }
  }, [messages, findScrollParent]);

  // Detect when user scrolls up significantly - remove spacer
  const handleScroll = useCallback((e) => {
    if (!hasUserSentMessage || scrollPositionAfterSendRef.current === null) return;

    const scrollParent = e.target;
    const currentScroll = scrollParent.scrollTop;
    const scrollThreshold = 200;

    if (scrollPositionAfterSendRef.current - currentScroll > scrollThreshold) {
      setHasUserSentMessage(false);
      scrollPositionAfterSendRef.current = null;
    }
  }, [hasUserSentMessage]);

  // Attach scroll listener to detect user scrolling up
  useEffect(() => {
    const scrollParent = findScrollParent();
    if (scrollParent && hasUserSentMessage) {
      scrollParent.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollParent.removeEventListener('scroll', handleScroll);
    }
  }, [hasUserSentMessage, handleScroll, findScrollParent, messages]);

  // Reset state when switching chats
  useEffect(() => {
    lastUserMessageIdRef.current = null;
    initialLoadDoneRef.current = false;
    scrollPositionAfterSendRef.current = null;
    hasScrolledToBottomRef.current = false;
    setHasUserSentMessage(false);
    setNewMessageId(null);
  }, [selectedChat]);

  return {
    chatContainerRef,
    hasUserSentMessage,
    newMessageId,
  };
};
